"use strict";
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const React = __importStar(require("react"));
const ElementSizeChangePoller_1 = require("./ElementSizeChangePoller");
const Interactable_1 = require("./Interactable");
const NoPanArea_1 = require("./NoPanArea");
const Pressable_1 = require("./Pressable");
const PressInterpreter_1 = require("./PressInterpreter");
const SpaceContext_1 = require("./SpaceContext");
const utils_1 = require("./utils");
const ViewPort_1 = require("./ViewPort");
/**
 * This component makes its children zoomable and pan-able.
 *
 * Please read the [Guide](../../Guide.md) for all the details on how to use
 * this.
 *
 * ## Props
 *
 * See `SpaceProps`.
 */
class Space extends React.PureComponent {
    constructor(props) {
        super(props);
        this.rootDivUniqueClassName = `react-zoomable-ui-${utils_1.generateRandomId()}`;
        this.constantStyles = `
.${this.rootDivUniqueClassName} {
  position: absolute;
  top: 0; bottom: 0; left: 0; right: 0;
  cursor: default;
}

.${this.rootDivUniqueClassName} > .react-zoomable-ui-inner-div {
  margin: 0; padding: 0; 
  transform-origin: 0% 0%;
  min-height: 100%;
  width: 100%;
}
`;
        /**
         * This should be called in some cases to tell the `Space` that its parent
         * element has resized. See the [Guide](../../Guide.md) for more info.
         */
        this.updateSize = () => {
            if (this.viewPort) {
                this.viewPort.updateContainerSize();
            }
        };
        this.createTransformStyle = () => {
            if (this.viewPort) {
                return {
                    transform: `scale(${this.viewPort.zoomFactor}) translate(${-1 * this.viewPort.left}px,${-1 *
                        this.viewPort.top}px)`,
                };
            }
            return undefined;
        };
        this.destroyViewPort = () => {
            var _a;
            if (this.viewPort) {
                this.viewPort.destroy();
                this.viewPort = undefined;
            }
            if (this.outerDivRef) {
                this.outerDivRef.removeEventListener('dragstart', this.handleDragStart);
            }
            (_a = this.elementSizeChangePoller) === null || _a === void 0 ? void 0 : _a.reset();
        };
        this.handleDragStart = (e) => {
            // This is the only way I have found that actually suppresses the default
            // handling of dragging on images, which interferes with our panning by
            // having a "ghost image" follow the pointer, across all browsers.
            // See this link for more info:
            // https://stackoverflow.com/questions/3873595/how-to-disable-firefoxs-default-drag-and-drop-on-all-images-behavior-with-jquer
            //
            // This additionally prevents another weird-o case of double clicking to
            // select text in Desktop Safari and then long clicking and dragging. This
            // will enter some sorta drag state where all the text is being dragged.
            // This is bad and it also conflicts with our <Pressable> components.
            if (e.target) {
                const interactableId = Interactable_1.getInteractableIdMostApplicableToElement(e.target);
                const interactable = (interactableId && this.interactableRegistry.get(interactableId)) || undefined;
                // Suppress the drag _unless_ it is within a no pan handling area, then
                // let it happen.
                if (interactable && interactable instanceof NoPanArea_1.NoPanArea) {
                    // Intentionally do nothing
                }
                else {
                    e.preventDefault();
                }
            }
        };
        this.handleDecideHowToHandlePress = (e, coordinates) => {
            if (this.props.onDecideHowToHandlePress) {
                const result = this.props.onDecideHowToHandlePress(e, coordinates);
                if (result) {
                    return result;
                }
            }
            const interactableId = Interactable_1.getInteractableIdMostApplicableToElement(e.target);
            const interactable = (interactableId && this.interactableRegistry.get(interactableId)) || undefined;
            if (e.type === 'mousedown') {
                const elementTagName = ((e.target && e.target.tagName) || '').toLowerCase();
                if (elementTagName === 'a' || elementTagName === 'button') {
                    // Prevent dragging on these elements A. the browsers may interpret the
                    // drag end as a click on it and B. desktop Safari (possibly others) has
                    // its own drag handling for links which conflicts with what we are
                    // doing.
                    return { ignorePressEntirely: true };
                }
            }
            if (interactable && interactable instanceof NoPanArea_1.NoPanArea) {
                return { ignorePressEntirely: true };
            }
            else if (interactable && interactable instanceof Pressable_1.Pressable) {
                return interactable.getPressHandlingConfig();
            }
            return undefined;
        };
        this.handleHover = (e, coordinates) => {
            const interactableId = Interactable_1.getInteractableIdMostApplicableToElement(e.target);
            const interactable = (interactableId && this.interactableRegistry.get(interactableId)) || undefined;
            if (interactable && interactable instanceof Pressable_1.Pressable) {
                if (interactable !== this.currentHoveredPressable) {
                    this.currentHoveredPressable = interactable;
                    this.currentHoveredPressable.setHovered(true);
                }
            }
            else if (this.currentHoveredPressable) {
                this.currentHoveredPressable.setHovered(false);
                this.currentHoveredPressable = undefined;
            }
            if (this.props.onHover) {
                this.props.onHover(e, coordinates);
            }
        };
        this.handleContextMenu = (e, coordinates) => {
            if (this.props.onContextMenu) {
                const result = this.props.onContextMenu(e, coordinates);
                e.preventDefault();
                if (result) {
                    return;
                }
            }
            const interactableId = Interactable_1.getInteractableIdMostApplicableToElement(e.target);
            const interactable = (interactableId && this.interactableRegistry.get(interactableId)) || undefined;
            if (interactable && interactable instanceof Pressable_1.Pressable && interactable.props.onContextMenu) {
                interactable.props.onContextMenu(coordinates);
                e.preventDefault();
                return;
            }
            // We have to prevent default this in a few cases on Android because it can
            // interfere w/ panning
            if (utils_1.browserIsAndroid) {
                if (interactable && interactable instanceof NoPanArea_1.NoPanArea) {
                    // Don't do anything
                }
                else {
                    e.preventDefault();
                }
                return;
            }
        };
        this.handleViewPortUpdated = () => {
            var _a, _b;
            this.setState({ transformStyle: this.createTransformStyle() });
            if (this.viewPort) {
                (_b = (_a = this.props).onUpdated) === null || _b === void 0 ? void 0 : _b.call(_a, this.viewPort);
            }
        };
        this.setOuterDivRefAndCreateViewPort = (ref) => {
            var _a, _b;
            this.destroyViewPort();
            this.outerDivRef = ref;
            if (this.outerDivRef) {
                this.viewPort = new ViewPort_1.ViewPort(this.outerDivRef, Object.assign(Object.assign({ onHover: this.handleHover, onContextMenu: this.handleContextMenu, onUpdated: this.handleViewPortUpdated }, this.pressInterpreter.pressHandlers), { treatTwoFingerTrackPadGesturesLikeTouch: this.props.treatTwoFingerTrackPadGesturesLikeTouch }));
                (_b = (_a = this.props).onCreate) === null || _b === void 0 ? void 0 : _b.call(_a, this.viewPort);
                this.outerDivRef.addEventListener('dragstart', this.handleDragStart);
                // Polling is optional because it is unnecessary if the only way the div's
                // size will change is with the window itself
                this.elementSizeChangePoller.update(this.outerDivRef, !!this.props.pollForElementResizing);
                const contextValue = {
                    rootDivUniqueClassName: this.rootDivUniqueClassName,
                    registerInteractable: i => this.interactableRegistry.set(i.id, i),
                    unregisterInteractable: i => this.interactableRegistry.delete(i.id),
                    viewPort: this.viewPort,
                };
                this.setState({
                    contextValue,
                    transformStyle: this.createTransformStyle(),
                });
            }
        };
        this.interactableRegistry = new Map();
        this.state = {};
        this.pressInterpreter = new PressInterpreter_1.PressInterpreter(this.handleDecideHowToHandlePress);
        // This won't actually start polling until we give it an element, and tell
        // it to start polling...
        this.elementSizeChangePoller = new ElementSizeChangePoller_1.ElementSizeChangePoller(this.updateSize);
    }
    componentDidUpdate(prevProps) {
        if (this.props.pollForElementResizing !== prevProps.pollForElementResizing) {
            this.elementSizeChangePoller.update(this.outerDivRef, !!this.props.pollForElementResizing);
        }
    }
    componentWillUnmount() {
        this.destroyViewPort();
    }
    render() {
        let transformedDivStyle = this.state.transformStyle;
        if (this.props.innerDivStyle) {
            transformedDivStyle = Object.assign(Object.assign({}, transformedDivStyle), this.props.innerDivStyle);
        }
        return (React.createElement("div", { ref: this.setOuterDivRefAndCreateViewPort, id: this.props.id, className: `react-zoomable-ui-outer-div ${this.rootDivUniqueClassName} ${this.props.className || ''}`, style: this.props.style },
            React.createElement("style", null, this.constantStyles),
            this.state.contextValue && (React.createElement(SpaceContext_1.SpaceContext.Provider, { value: this.state.contextValue },
                React.createElement("div", { className: `react-zoomable-ui-inner-div ${this.props.innerDivClassName || ''}`, style: transformedDivStyle }, this.props.children)))));
    }
}
exports.Space = Space;
//# sourceMappingURL=Space.js.map